import { ReactNode } from 'react';
import { Trans } from '@lingui/react/macro';

import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import { ArticleConditionForm, ArticleConditionFormType, ConditionForm } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';

import { ConditionQuestionCheckbox } from './ConditionQuestionCheckbox';
import { ConditionQuestionMultiQuantifiableSelect } from './ConditionQuestionMultiQuantifiableSelect';
import { ConditionQuestionRadio } from './ConditionQuestionRadio';
import { ConditionQuestionTextAndSelect } from './ConditionQuestionTextAndSelect';

export type ConditionFormQuestion = ConditionForm['questions'][number];

export const getSubQuestion = ({
  question,
  articleConditionForm,
}: {
  question: ConditionFormQuestion;
  articleConditionForm?: ArticleConditionForm;
}) => {
  const chosenAnswerId = articleConditionForm?.articleConditions?.find(
    (articleCondition) => articleCondition.questionId === question.id
  )?.answerId;

  const chosenAnswer = question.answers?.find((answer) => answer.id === chosenAnswerId);

  return chosenAnswer?.subQuestion ?? null;
};

export const ConditionQuestion = ({
  question,
  parentQuestion,
  type,
  articleConditionForms,
}: {
  question: ConditionFormQuestion;
  parentQuestion?: ConditionFormQuestion;
  type: ArticleConditionFormType;
  articleConditionForms: ArticleConditionForm[];
}) => {
  const { view } = useArticleContext();

  const editable =
    type === 'initial-condition' ? view.initialCondition.editable : view.qualityControl.editable;

  const editableArticleConditionForm = articleConditionForms.find(
    (articleConditionForm) => !articleConditionForm.completedAt
  );

  const subQuestion = getSubQuestion({
    question,
    articleConditionForm: editableArticleConditionForm,
  });

  let children: ReactNode;

  if (question.type === 'radio') {
    children = (
      <ConditionQuestionRadio
        type={type}
        question={question}
        parentQuestion={parentQuestion}
        articleConditionForms={articleConditionForms}
      />
    );
  } else if (question.type === 'checkbox') {
    children = (
      <ConditionQuestionCheckbox
        type={type}
        question={question}
        parentQuestion={parentQuestion}
        editableArticleConditionForm={editableArticleConditionForm}
      />
    );
  } else if (question.type === 'text-and-select') {
    children = (
      <ConditionQuestionTextAndSelect
        type={type}
        question={question}
        parentQuestion={parentQuestion}
        editableArticleConditionForm={editableArticleConditionForm}
      />
    );
  } else if (question.type === 'multi-quantifiable-select') {
    children = (
      <ConditionQuestionMultiQuantifiableSelect
        type={type}
        question={question}
        parentQuestion={parentQuestion}
        editableArticleConditionForm={editableArticleConditionForm}
      />
    );
  }

  return (
    <Stack
      gap={editable ? '1rem' : undefined}
      padding={parentQuestion ? undefined : '12px 12px 0 12px'}
    >
      {children}
      {subQuestion && (
        <ConditionQuestion
          type={type}
          question={subQuestion}
          parentQuestion={question}
          articleConditionForms={articleConditionForms}
        />
      )}
    </Stack>
  );
};

export const ConditionQuestionError = ({
  question,
  type,
}: {
  question: ConditionFormQuestion;
  type: ArticleConditionFormType;
}) => {
  const { errors } = useArticleContext();
  const questionErrors =
    type === 'quality-control'
      ? errors.qualityControl?.questions?.find(({ id }) => id === question.id)
      : errors.condition?.questions?.find(({ id }) => id === question.id);

  if (questionErrors?.hasError) {
    return (
      <Message type="error">
        {questionErrors.noAnswers || questionErrors.subQuestion?.noAnswers ? (
          type === 'quality-control' ? (
            <Trans id="condition-question-quality-control.errors.no-answers">
              Please specify the condition of the item
            </Trans>
          ) : (
            <Trans id="condition-question.errors.no-answers">
              Please specify the initial condition of the item
            </Trans>
          )
        ) : (
          <Trans id="condition-question.errors.invalid-answer">
            Please fill the required fields
          </Trans>
        )}
      </Message>
    );
  }

  return null;
};

export const useQuestionErrors = ({
  question,
  parentQuestion,
  type,
}: {
  question?: ConditionFormQuestion;
  parentQuestion?: ConditionFormQuestion;
  type: ArticleConditionFormType;
}) => {
  const { errors } = useArticleContext();
  const questionErrors =
    type === 'quality-control'
      ? (errors.qualityControl?.questions?.find(({ id }) => id === question?.id) ??
        errors.qualityControl?.questions?.find(({ id }) => id === parentQuestion?.id)?.subQuestion)
      : (errors.condition?.questions?.find(({ id }) => id === question?.id) ??
        errors.condition?.questions?.find(({ id }) => id === parentQuestion?.id)?.subQuestion);

  return questionErrors;
};
